/* eslint-disable operator-linebreak */
/* eslint-disable no-extra-parens */
import { useEffect, useRef } from 'react';
import type { Comscore, IStoreState } from '../../components/common/interfaces/types';
import { WbStatePrivacyCookieKey } from '../../components/common/interfaces/types';
import { reduxConnect } from '../../common';
import { getStatePrivacyPolicyCookie } from '../../common/utilities/ccpaHelper';

declare global {
    interface Window {
        _comscore: any;
        COMSCORE: {
            beacon: (params: Record<string, any>) => void;
            purge: any;
        };
    }
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { statePrivacy, legal, appState } = storeState || {};
    const { comscoreConfig, user, pageId } = appState?.appState || {};
    const { complianceRequirements } = legal || {};
    const { dataPrivacyEnabled, sensitiveDataRestrictionEnabled, disableAds } = complianceRequirements || {};

    return {
        comscoreConfig,
        disableAds,
        dataPrivacyEnabled,
        sensitiveDataRestrictionEnabled,
        currentLocationSlug: user?.ActiveLocation?.Locality?.SlugName,
        gpcCheckComplete: statePrivacy?.gpcCheckComplete,
        gpcEnabled: statePrivacy?.gpcEnabled,
        statePrivacyUpdated: statePrivacy?.refreshTargetingAPICalls,
        pageId
    };
};

const mapDispatchToProps = {
};

type ComScoreWebProps = {
    comscoreConfig: Comscore;
    disableAds: boolean;
    dataPrivacyEnabled: boolean;
    sensitiveDataRestrictionEnabled: boolean;
    currentLocationSlug: string;
    gpcCheckComplete: boolean;
    gpcEnabled: boolean;
    pageId: string;
    statePrivacyUpdated: boolean;
}

export const ComScoreWeb = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ 
    comscoreConfig, 
    disableAds,
    gpcCheckComplete,
    gpcEnabled,
    dataPrivacyEnabled, 
    sensitiveDataRestrictionEnabled,
    statePrivacyUpdated,
    pageId,
    currentLocationSlug }: ComScoreWebProps) => {

    const isComscoreInitialLoadComplete = useRef(false);
    const prevProps = useRef<{
        pageId: string | null;
        currentLocationSlug: string | null;
        statePrivacyUpdated?: boolean;
    }>({
        pageId: null,
        currentLocationSlug: null
    });
    const {
        IsEnabled: isComscoreEnabled, 
        TagTypeC1Param: tagTypeC1Param, 
        FirstPartyCookieName: firstPartyCookieName, 
        EnableFirstPartyCookie: enableFirstPartyCookie, 
        PublisherIdC2Param: publisherIdC2Param } = comscoreConfig || {};

    useEffect(() => {
        if (!gpcCheckComplete || disableAds || !isComscoreEnabled || !currentLocationSlug || !pageId || isComscoreInitialLoadComplete.current) {
            return;
        }
        
        const comscoreScriptId = 'wb-web-1-comescore-script-id';
        const comscoreScript = document.getElementById(comscoreScriptId);
        if (!comscoreScript && publisherIdC2Param && !isComscoreInitialLoadComplete.current) {

            let comscoreOptions = {
                c1: tagTypeC1Param, 
                c2: publisherIdC2Param, 
                cs_fpdm: '*null', 
                cs_fpdt: '*null',
                options: {
                    enableFirstPartyCookie: enableFirstPartyCookie
                }
            } as Record<string, any>;

            if ((dataPrivacyEnabled || sensitiveDataRestrictionEnabled)) {
                const statePrivacyCookie = getStatePrivacyPolicyCookie(WbStatePrivacyCookieKey) || {};
                
                if (statePrivacyCookie?.doNotSell || statePrivacyCookie?.limitUseOfPI) {
                    comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: '0'
                    };
                } else if ((statePrivacyCookie?.doNotSell === false && statePrivacyCookie?.limitUseOfPI === undefined) ||  
                    (statePrivacyCookie?.doNotSell === undefined && statePrivacyCookie?.limitUseOfPI === false) || 
                    (statePrivacyCookie?.doNotSell === false && statePrivacyCookie?.limitUseOfPI === false)) {
                    comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: '1'
                    };
                } else {
                     comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: ''
                    };
                }
            }

            // GPC takes precedence over every other State Privacy opt out functionality
            if (navigator.globalPrivacyControl) {
                comscoreOptions = {
                    ...comscoreOptions,
                    cs_ucfr: '0'
                };
            }
            window._comscore = window._comscore || [];
            window._comscore.push(comscoreOptions);

            (function() {
                const scriptTag = document.createElement('script');
                scriptTag.src = `https://sb.scorecardresearch.com/cs/${publisherIdC2Param}/beacon.js`;
                scriptTag.async = true;
                scriptTag.id = comscoreScriptId;
        
                scriptTag.onload = () => {
                    isComscoreInitialLoadComplete.current = true;
                    prevProps.current = {
                        pageId,
                        currentLocationSlug,
                        statePrivacyUpdated
                    };
                };
        
                scriptTag.onerror = () => {
                    console.error('COMSCORE__DEBUG_WEB_1:: Failed to load comscore script tag');
                };
                document.head.appendChild(scriptTag);
            })();
        }
    }, [
        isComscoreEnabled,
        disableAds,
        publisherIdC2Param,
        tagTypeC1Param,
        firstPartyCookieName,
        enableFirstPartyCookie,
        gpcCheckComplete,
        dataPrivacyEnabled,
        sensitiveDataRestrictionEnabled,
        pageId,
        currentLocationSlug
    ]);

    useEffect(() => {
        if (isComscoreInitialLoadComplete.current &&
            window.COMSCORE &&
            pageId &&
            currentLocationSlug &&
            prevProps.current.pageId &&
            prevProps.current.currentLocationSlug &&
            (prevProps.current.pageId !== pageId || 
            prevProps.current.currentLocationSlug !== currentLocationSlug)
        ) {
            let comscoreOptions = {
                c1: tagTypeC1Param, 
                c2: publisherIdC2Param, 
                cs_fpdm: '*null', 
                cs_fpdt: '*null',
                options: {
                    enableFirstPartyCookie: enableFirstPartyCookie
                }
            } as Record<string, any>;

            if ((dataPrivacyEnabled || sensitiveDataRestrictionEnabled)) {
                const statePrivacyCookie = getStatePrivacyPolicyCookie(WbStatePrivacyCookieKey) || {};
                
                if (statePrivacyCookie?.doNotSell || statePrivacyCookie?.limitUseOfPI) {
                    comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: '0'
                    };
                } else if ((statePrivacyCookie?.doNotSell === false && statePrivacyCookie?.limitUseOfPI === undefined) ||  
                    (statePrivacyCookie?.doNotSell === undefined && statePrivacyCookie?.limitUseOfPI === false) || 
                    (statePrivacyCookie?.doNotSell === false && statePrivacyCookie?.limitUseOfPI === false)) {
                    comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: '1'
                    };
                } else {
                     comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: ''
                    };
                }
                // GPC takes precedence over every other State Privacy opt out functionality
                if (navigator.globalPrivacyControl) {
                    comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: '0'
                    };
                }
            }
            window.COMSCORE?.beacon?.(comscoreOptions);
        }
        
        if (pageId && currentLocationSlug) {
            prevProps.current.pageId = pageId;
            prevProps.current.currentLocationSlug = currentLocationSlug;
        }
    }, [pageId, 
        currentLocationSlug, 
        tagTypeC1Param, 
        publisherIdC2Param, 
        enableFirstPartyCookie, 
        sensitiveDataRestrictionEnabled, 
        dataPrivacyEnabled]);

    useEffect(() => {
        if (isComscoreInitialLoadComplete.current &&
            window.COMSCORE &&
            pageId &&
            currentLocationSlug &&
            statePrivacyUpdated !== prevProps.current.statePrivacyUpdated &&
            prevProps.current.pageId &&
            prevProps.current.currentLocationSlug &&
            (prevProps.current.pageId === pageId &&
            prevProps.current.currentLocationSlug === currentLocationSlug)
        ) {
            let comscoreOptions = {
                c1: tagTypeC1Param, 
                c2: publisherIdC2Param, 
                cs_fpdm: '*null', 
                cs_fpdt: '*null',
                options: {
                    enableFirstPartyCookie: enableFirstPartyCookie
                }
            } as Record<string, any>;

            if ((dataPrivacyEnabled || sensitiveDataRestrictionEnabled)) {
                const statePrivacyCookie = getStatePrivacyPolicyCookie(WbStatePrivacyCookieKey) || {};
                if (statePrivacyCookie?.doNotSell || statePrivacyCookie?.limitUseOfPI) {
                    comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: '0'
                    };
                } else if ((statePrivacyCookie?.doNotSell === false && statePrivacyCookie?.limitUseOfPI === undefined) ||  
                    (statePrivacyCookie?.doNotSell === undefined && statePrivacyCookie?.limitUseOfPI === false) || 
                    (statePrivacyCookie?.doNotSell === false && statePrivacyCookie?.limitUseOfPI === false)) {
                    comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: '1'
                    };
                } else {
                     comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: ''
                    };
                }

                // GPC takes precedence over every other State Privacy opt out functionality
                if (navigator.globalPrivacyControl) {
                    comscoreOptions = {
                        ...comscoreOptions,
                        cs_ucfr: '0'
                    };
                }
            }
            window.COMSCORE?.beacon?.(comscoreOptions);
        }
        
        if (pageId && currentLocationSlug) {
            prevProps.current.statePrivacyUpdated = statePrivacyUpdated;
        }
    }, [statePrivacyUpdated,
        pageId, 
        currentLocationSlug, 
        tagTypeC1Param, 
        publisherIdC2Param, 
        enableFirstPartyCookie, 
        sensitiveDataRestrictionEnabled, 
        dataPrivacyEnabled]);

    return null;
});

ComScoreWeb.displayName = 'ComScoreWeb';